import React, { useState } from 'react';
import {
  Alert, Autocomplete, Button, Checkbox, FormControlLabel,
  Stack, TextField, Typography,
} from '@mui/material';

const decoratorLabel = (decorator) => decorator?.title || decorator?.name || 'Decorator';
const companyAddressLabel = (address) => [
  address.label || address.companyName,
  address.addressLine1,
  address.city,
  address.stateProvince,
  address.postalCode,
].filter(Boolean).join(' · ');

export default function DecoratorDestinationRulesEditor({
  decorators = [], companyAddresses = [], rules = {}, onChange, disabled = false,
}) {
  const [selectedId, setSelectedId] = useState('');
  const selected = decorators.find((decorator) => String(decorator.id) === selectedId) || null;
  const rule = selected ? rules[String(selected.id)] : null;
  const selectedCompanyAddress = rule?.companyAddressId == null ? null
    : companyAddresses.find((address) => Number(address.id) === Number(rule.companyAddressId));
  const updateRule = (patch) => onChange({
    ...rules,
    [selectedId]: { ...rule, ...patch },
  });
  const addRule = () => onChange({
    ...rules,
    [selectedId]: {
      companyAddressId: null,
      lockSalesAddress: true,
      visibleInShipping: true,
      poRecipientName: '',
    },
  });
  const removeRule = () => {
    const nextRules = { ...rules };
    delete nextRules[selectedId];
    onChange(nextRules);
  };

  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="subtitle1" fontWeight={600}>Decorator Shipping Rules</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Set shipping visibility and permissions for decorators. Receiving addresses are optional until configured in Decorator Matrix.
        </Typography>
      </div>
      <Autocomplete
        options={decorators}
        value={selected}
        disabled={disabled}
        onChange={(_event, value) => setSelectedId(value ? String(value.id) : '')}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
        getOptionLabel={decoratorLabel}
        renderOption={(props, option) => (
          <li {...props} key={String(option.id)}>
            <Stack>
              <Typography variant="body2">{decoratorLabel(option)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {rules[String(option.id)]
                  ? rules[String(option.id)].visibleInShipping === false ? 'Hidden from shipping choices' : 'Shipping rule configured'
                  : 'No shipping rule'}
              </Typography>
            </Stack>
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Decorator" placeholder="Choose a decorator" />}
        noOptionsText="No decorators available for this instance"
      />
      {selected && !rule && (
        <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            This decorator uses the existing shipping behavior until a rule is added.
          </Typography>
          <Button variant="outlined" onClick={addRule} disabled={disabled}>Add Shipping Rule</Button>
        </Stack>
      )}
      {selected && rule && (
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            label="Receiving address"
            value={rule.companyAddressId != null
              ? (selectedCompanyAddress ? companyAddressLabel(selectedCompanyAddress) : 'Configured company address unavailable')
              : (selected.addressId ? `Decorator's saved address (#${selected.addressId})` : 'No address configured')}
            InputProps={{ readOnly: true }}
            helperText="Manage this address under Company Settings → Decorator Matrix. Company-linked addresses come from Company Contact Info."
          />
          {rule.companyAddressId == null && !selected.addressId && rule.visibleInShipping !== false && (
            <Alert severity="info">No receiving address linked yet. This rule can still be saved, and Shipping can be saved without selecting a receiving address. Set the address in Decorator Matrix before issuing the supplier PO.</Alert>
          )}
          <FormControlLabel
            control={<Checkbox checked={rule.lockSalesAddress === true} disabled={disabled} onChange={(event) => updateRule({ lockSalesAddress: event.target.checked })} />}
            label="Lock receiving address for sales reps"
          />
          <Typography variant="caption" color="text.secondary">
            Administrators and super-administrators can change the receiving address.
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={rule.visibleInShipping !== false} disabled={disabled} onChange={(event) => updateRule({ visibleInShipping: event.target.checked })} />}
            label="Show in shipping choices"
          />
          <TextField
            label="Name on PO (optional)"
            value={rule.poRecipientName || ''}
            disabled={disabled}
            onChange={(event) => updateRule({ poRecipientName: event.target.value })}
            inputProps={{ maxLength: 255 }}
            helperText={`Leave blank to use ${decoratorLabel(selected)}.`}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button onClick={removeRule} disabled={disabled}>Remove Shipping Rule</Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
